import React, { useState } from "react";
import { Button, Popover, Space, Spin, Typography, notification } from "antd";
import { SafetyCertificateOutlined, CopyOutlined } from "@ant-design/icons";
import lib from "../../assets/utils/lib";
import { hasAnyPermission } from "../../assets/utils/permissions";

const { Text } = Typography;

export const ONBOARDING_OTP_REVEAL_PERMS = ["onboarding.otp_reveal", "admin.*"];

export function canRevealOnboardingOtp(permissions) {
  return hasAnyPermission(ONBOARDING_OTP_REVEAL_PERMS, permissions || {});
}

function copyText(code) {
  if (!code) return;
  if (navigator?.clipboard?.writeText) {
    navigator.clipboard
      .writeText(code)
      .then(() => notification.success({ message: "Copied", placement: "topLeft", duration: 1.5 }));
  }
}

function formatIssuedAt(value) {
  if (!value) return null;
  try {
    const d = new Date(String(value).replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString();
  } catch {
    return String(value);
  }
}

/**
 * "Get code" popover: reveals every pending one-time code for a phone number
 * without resending anything — registration phone verification, enrollee
 * account sign-up / password reset and policy-account password reset. The
 * codes are already stored before the SMS is attempted, so there's nothing
 * to resend, only to look up. The full OTP lookup page is /admin/onboarding-otp.
 */
export function OnboardingOtpRevealAction({ phone, size = "small" }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const trimmedPhone = String(phone || "").trim();

  const fetchCode = async () => {
    if (!trimmedPhone) return;
    setLoading(true);
    setResult(null);
    try {
      const resp = await lib.getOnboardingOtp(trimmedPhone);
      const body = resp?.data;
      if (Number(body?.error) === 0 && body?.data?.code) {
        const codes = Array.isArray(body.data.codes) ? body.data.codes.filter((c) => c?.code) : [];
        setResult({
          code: body.data.code,
          issuedAt: body.data.issued_at,
          codes: codes.length ? codes : [{ code: body.data.code, issued_at: body.data.issued_at, kind_label: 'Pending code' }],
        });
      } else {
        setResult({ notFound: true, message: body?.message });
      }
    } catch (e) {
      const status = Number(e?.response?.status || 0);
      if (status === 404) {
        setResult({ notFound: true, message: e?.response?.data?.message });
      } else {
        setResult({
          error: e?.response?.data?.message || e?.message || "Could not look up the code",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const content = (
    <div style={{ maxWidth: 260 }} onClick={(e) => e.stopPropagation()}>
      {loading ? (
        <Spin size="small" />
      ) : result?.code ? (
        <Space direction="vertical" size={10} style={{ width: "100%" }}>
          {result.codes.map((entry, i) => (
            <Space key={`${entry.kind || "code"}-${i}`} direction="vertical" size={2} style={{ width: "100%" }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {entry.kind_label || "Pending code"}
                {entry.expired ? " (expired)" : ""}
              </Text>
              <Space size={4} align="center">
                <Text code style={{ fontSize: 16, fontWeight: 700 }}>
                  {entry.code}
                </Text>
                <Button type="link" size="small" icon={<CopyOutlined />} onClick={() => copyText(entry.code)}>
                  Copy
                </Button>
              </Space>
              {formatIssuedAt(entry.issued_at) ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Sent: {formatIssuedAt(entry.issued_at)}
                </Text>
              ) : null}
            </Space>
          ))}
        </Space>
      ) : result?.notFound ? (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {result.message ||
            "No pending code for this phone. They may have already verified, or need to request a new one."}
        </Text>
      ) : result?.error ? (
        <Text type="danger" style={{ fontSize: 12 }}>
          {result.error}
        </Text>
      ) : (
        <Text type="secondary" style={{ fontSize: 12 }}>
          Loading…
        </Text>
      )}
    </div>
  );

  return (
    <Popover
      trigger="click"
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) fetchCode();
      }}
      content={content}
      placement="bottomRight"
    >
      <Button
        type="link"
        size={size}
        className="listing-card__action"
        icon={<SafetyCertificateOutlined />}
        onClick={(e) => e.stopPropagation()}
        disabled={!trimmedPhone}
      >
        Get code
      </Button>
    </Popover>
  );
}

export default OnboardingOtpRevealAction;
